  import React, { useState, useEffect, useMemo } from "react";
  import { useLocation, useNavigate } from "react-router-dom";
  import { useSelector } from "react-redux";
  import { toast } from "sonner";
  import { Loader2 } from "lucide-react";

  import { useGetCourseByIdQuery } from "@/features/api/courseApi";
  import { useGetMockTestByIdQuery } from "@/features/api/mockTestApi";
  import { useCreateOrderMutation } from "@/features/api/orderApi";

  import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
  } from "@/components/ui/card";

  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";

  export default function CheckoutPage() {

    const location = useLocation();
    const navigate = useNavigate();

    const { user, isAuthenticated } = useSelector((state) => state.auth || {});

    const [isRedirecting, setIsRedirecting] = useState(false);

    /* ================= URL PARAM ================= */

    const params = new URLSearchParams(location.search);
  const id = params.get("id");
  const type = params.get("type");

  useEffect(() => {
    if (!id || !type) {
      toast.error("Invalid checkout link");
      navigate("/", { replace: true });
    } else if (!isAuthenticated) {
      navigate("/login", {
        state: {
          redirectTo: location.pathname + location.search,
        },
      });
    }
  }, [id, type, isAuthenticated, location, navigate]);

    /* ================= FETCH COURSE ================= */

  const {
    data: courseData,
    isLoading: courseLoading
  } = useGetCourseByIdQuery (id, {
    skip: type !== "course" || !id,
  });

    /* ================= FETCH MOCK TESTS ================= */
  const {
    data: mockData,
    isLoading: mockLoading
  } = useGetMockTestByIdQuery(id, {
    skip: type !== "mock" || !id,
  });

    /* ================= IMAGE NORMALIZER ================= */

    const media = (url) =>
      url?.startsWith("/uploads")
        ? `https://fetc.in${url}`
        : url || "";

    /* ================= PRODUCT DETECTION ================= */

    const product = useMemo(() => {

if (!id || !type) return null;
if (type === "course" && !courseData) return null;
if (type === "mock" && !mockData) return null;

    if (type === "course" && courseData?.course) {
      return {
        id: courseData.course._id,
        name: courseData.course.title,
        price: Number(courseData.course.price) || 0,
        image: media(courseData.course.featuredImage),
        productType: "COURSE",
      };
    }

    if (type === "mock" && mockData?.mockTest) {
      return {
        id: mockData.mockTest._id,
        name: mockData.mockTest.name,
        price: Number(mockData.mockTest.price) || 0,
        image: media(mockData.mockTest.image),
        productType: "MOCK_TEST", // 🔥 FIXED
      };
    }

    return null;

  }, [courseData, mockData, id, type]);

    /* ================= INVALID PRODUCT ================= */

  useEffect(() => {

    if (
      (type === "course" && !courseLoading && !courseData?.course) ||
      (type === "mock" && !mockLoading && !mockData?.mockTest)
    ) {
      toast.error("Invalid product.");
      navigate("/", { replace: true });
    }

  }, [type, courseLoading, mockLoading, courseData, mockData, navigate]);

    /* ================= FORM STATE ================= */

    const [formData, setFormData] = useState({
      name: "",
      email: "",
      phone: "",
    });

    const [termsAccepted, setTermsAccepted] = useState(false);

    /* ================= AUTO FILL USER ================= */

    useEffect(() => {

      if (isAuthenticated && user) {
        setFormData({
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.phone || user?.tel || "",
        });
      }

    }, [isAuthenticated, user]);

    /* ================= PRICE ================= */

  const price = useMemo(() => Number(product?.price || 0), [product]);
    const cgst = price > 0 ? Number((price * 0.09).toFixed(2)) : 0;
    const sgst = price > 0 ? Number((price * 0.09).toFixed(2)) : 0;

    const totalAmount =
      price > 0 ? Number((price + cgst + sgst).toFixed(2)) : 0;

    /* ================= API ================= */

    const [createOrder, { isLoading }] = useCreateOrderMutation();

    /* ================= INPUT ================= */

    const handleInputChange = (e) => {

      const { id, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));

    };

    /* ================= VALIDATION ================= */

    const validateForm = () => {

      const name = formData.name.trim();
      const email = formData.email.trim();
      const phone = formData.phone.replace(/\D/g, "");

      if (!name || !email || !phone) {
        toast.error("All fields are required.");
        return false;
      }

      if (!/^\d{10}$/.test(phone)) {
        toast.error("Enter valid 10 digit phone number.");
        return false;
      }

      if (!termsAccepted) {
        toast.error("Please accept Terms & Conditions.");
        return false;
      }

      return true;

    };

    /* ================= SUBMIT ================= */

    const handleSubmit = async (e) => {

      e.preventDefault();

      if (isRedirecting) return;
      if (!validateForm()) return;
  if (!product?.id || !product?.productType) {
    toast.error("Invalid product");
    return;
  }
      const cleanPhone = formData.phone.replace(/\D/g, "");

      try {

        setIsRedirecting(true);

        const response = await createOrder({
          phone: cleanPhone,
          courseId: product.id,
          name: formData.name.trim(),
          email: formData.email.trim(),
          productType: product.productType,
        }).unwrap();

        if (response?.redirectUrl) {
          window.location.href = response.redirectUrl;
          return;
        }

        if (response?.transactionId) {
          navigate(`/payment-status?orderId=${response.transactionId}`, {
            replace: true,
          });
          return;
        }

        setIsRedirecting(false);
        toast.error("Unable to initiate payment");

      } catch (error) {

        setIsRedirecting(false);
        console.error("Payment Error:", error);
        toast.error(error?.data?.message || "Payment failed");

      }

    };

    /* ================= LOADING ================= */

  if (
    (type === "course" && courseLoading) ||
    (type === "mock" && mockLoading)
  ){
      return (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin h-6 w-6" />
        </div>
      );
    }

  if (!product) {
    return (
      <div className="text-center py-20 text-gray-500">
        {type === "course" && !courseLoading && !courseData?.course
          ? "Course not found"
          : type === "mock" && !mockLoading && !mockData?.mockTest
          ? "Mock test not found"
          : "Loading product..."}
      </div>
    );
  }
    /* ================= UI ================= */

    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-8">

          {/* BILLING */}

          <div className="lg:col-span-2">

            <Card>

              <CardHeader>
                <CardTitle>Billing Details</CardTitle>
                <CardDescription>
                  Enter your details to complete purchase
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">

                <div className="grid md:grid-cols-2 gap-6">

                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" value={formData.name} onChange={handleInputChange}/>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={formData.email} onChange={handleInputChange}/>
                  </div>

                </div>

                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" value={formData.phone} onChange={handleInputChange}/>
                </div>

                <div className="flex items-start gap-3">

                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e)=>setTermsAccepted(e.target.checked)}
                    className="mt-1 h-5 w-5 accent-blue-600"
                  />

                  <Label className="text-sm">
                    I agree to{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      Terms & Conditions
                    </a>
                  </Label>

                </div>

              </CardContent>

            </Card>

          </div>

          {/* SUMMARY */}

          <div>

            <Card className="sticky top-6">

              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">

                {product.image && (
                  <img src={product.image} alt={product.name} className="rounded-lg"/>
                )}

                <h2 className="font-semibold text-lg">{product.name}</h2>

                <div className="text-sm space-y-2">

                  <div className="flex justify-between">
                    <span>Price</span>
                    <span>₹{price.toFixed(2)}</span>
                  </div>

                  {price > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span>CGST (9%)</span>
                        <span>₹{cgst.toFixed(2)}</span>
                      </div>

                      <div className="flex justify-between">
                        <span>SGST (9%)</span>
                        <span>₹{sgst.toFixed(2)}</span>
                      </div>
                    </>
                  )}

                  <hr/>

                  <div className="flex justify-between font-bold">
                    <span>Total</span>
                    <span>₹{totalAmount.toFixed(2)}</span>
                  </div>

                </div>

                <Button type="submit" disabled={isLoading || isRedirecting} className="w-full">

                  {(isLoading || isRedirecting) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
                  )}

                {isRedirecting
                  ? "Redirecting..."
                  : totalAmount === 0
                  ? "Enroll Now"
                  : "Proceed to Pay"}

                </Button>

              </CardContent>

            </Card>

          </div>

        </form>

      </div>
    );
  }

